import { filterContactInput, contactErrors } from "../../../../utils/contact-validation.ts";
import { useRef, useState, type FormEvent } from "react";
import { Button, Modal, Select, TextField, Tabs, TabsList, TabsTrigger, TabsContent, type SelectOption } from "../../../ui/index.ts";
import type { ConsultantInvitation, ConsultantType } from "./types.ts";
import { inviteAgency, inviteConsultant } from "./invitation-api.ts";
import { useInvitationContacts } from "./use-invitation-contacts.ts";
import { showError, showSuccess } from "../../../../utils/toast.ts";
import { capitalizeNameWords } from "../../../../utils/helpers.ts";
import styles from "./consultant.module.css";

interface Props {
  region?: "INDIA" | "US";
  consultantType: ConsultantType;
  requestorOptions: SelectOption[];
  onClose: () => void;
  onSave: (draft: ConsultantInvitation) => void;
}

export function InviteConsultantModal({ consultantType, region, /* requestorOptions, */ onClose, onSave }: Props) {
  const contacts = useInvitationContacts();
  const [busy, setBusy] = useState(false);
  const [consultantSent, setConsultantSent] = useState(false);
  const reference = useRef<string | null>(null);
  const inFlight = useRef(false);
  const isVendor = consultantType === "vendor";
  const [activeTab, setActiveTab] = useState("consultant");
  const [agency, setAgency] = useState({ legalCompanyName: "", agencyEmail: "", contactName: "" });
  const [values, setValues] = useState({
    consultantName: "", personalEmail: "", region: region ?? "", agencyName: "",
    agencyContactNumber: "", pointOfContact: "", requestorId: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const update = (key: keyof typeof values, value: string) => {
    value = filterContactInput(key, value);
    setValues((current) => ({
      ...current, [key]: value,
      ...(key === "region" && value !== "US" ? { agencyName: "", agencyContactNumber: "" } : {}),
    }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };

  const handleNext = () => {
  const next: Record<string, string> = contactErrors(values);

  if (!values.consultantName.trim()) {
    next.consultantName = "Enter consultant Name.";
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.personalEmail.trim())) {
    next.personalEmail = "Enter a valid personal email.";
  }

  if (!values.region) {
    next.region = "Select a region.";
  }

  if (!contacts.options.some((option) => option.value === values.pointOfContact)) {
    next.pointOfContact = "Enter a point of contact.";
  }

  setErrors(next);

  if (Object.keys(next).length) {
    setActiveTab("consultant");
    return;
  }

  setActiveTab("agency");
};

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (inFlight.current) return;
    const next: Record<string, string> = contactErrors(values);
    if (!values.consultantName.trim()) next.consultantName = "Enter consultant Name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.personalEmail.trim())) next.personalEmail = "Enter a valid personal email.";
    if (!values.region) next.region = "Select a region.";
    if (!contacts.options.some((option) => option.value === values.pointOfContact)) next.pointOfContact = "Enter a point of contact.";
    // Requestor Name is temporarily hidden.
    // if (!values.requestorId) next.requestorId = "Select a requestor.";
    const hasConsultantErrors = Object.keys(next).length > 0;
    if (isVendor) {
      Object.assign(next, contactErrors({ contactName: agency.contactName }));
      if (!agency.contactName.trim()) next.contactName = "Enter the agency contact name.";
      if (!agency.legalCompanyName.trim()) next.legalCompanyName = "Enter the legal company name.";
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(agency.agencyEmail.trim())) next.agencyEmail = "Enter a valid agency email address.";
      if (values.region === "US") {
        if (!values.agencyName.trim()) {
          next.agencyName = "Enter the agency name.";
        }

        if (!values.agencyContactNumber.trim()) {
          next.agencyContactNumber = "Enter the agency contact number.";
        }
      }
    }
    setErrors(next);
    if (Object.keys(next).length) {
      if (isVendor) setActiveTab(hasConsultantErrors ? "consultant" : "agency");
      return;
    }
    inFlight.current = true;
    setBusy(true);
    try {
      if (!consultantSent) {
        reference.current = await inviteConsultant({ ...values, consultantType });
        setConsultantSent(true);
      }
      if (isVendor) {
        if (!reference.current) throw new Error("Consultant invitation succeeded, but the response did not include onboardingReferenceId. Agency invitation was not sent. Check the response before retrying.");
        await inviteAgency(reference.current, agency.contactName, agency.agencyEmail, values.pointOfContact);
      }
      showSuccess(isVendor ? "Consultant and agency invitations sent." : "Consultant invitation sent.");
      onSave({
        ...values,
        consultantName: values.consultantName.trim(),
        personalEmail: values.personalEmail.trim(),
        ...(isVendor ? { legalCompanyName: agency.legalCompanyName.trim(), agencyEmail: agency.agencyEmail.trim() } : {}),
        id: reference.current ?? crypto.randomUUID(),
        status: "Invited",
        consultantType,
      });
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to send invitation.");
      if (isVendor) setActiveTab("agency");
    } finally {
      setBusy(false);
      inFlight.current = false;
    }
  };

  return (
    <Modal open onClose={() => { if (!inFlight.current) onClose(); }} title="Invite Consultant" size="lg" footer={
      <div className={styles.actions}>
        <Button variant="light-grey" disabled={busy} onClick={onClose}>Cancel</Button>
{isVendor && activeTab === "consultant" && !consultantSent ? (
  <Button
    type="button"
    disabled={busy || contacts.loading}
    onClick={handleNext}
  >
    Next
  </Button>
) : (
  <Button
    type="submit"
    form="invite-consultant-form"
    disabled={busy || contacts.loading || (consultantSent && !reference.current)}
  >
    {busy ? "Sending..." : consultantSent ? "Retry Agency Invitation" : "Send Invitation"}
  </Button>
)}      </div>
    }>
      <form id="invite-consultant-form" onSubmit={(event) => void submit(event)} noValidate>
        {consultantSent && isVendor && <p role="status">Consultant invitation sent. Only the agency invitation remains.</p>}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          {isVendor && <TabsList>
            <TabsTrigger value="consultant">Consultant</TabsTrigger>
            <TabsTrigger value="agency">Agency</TabsTrigger>
          </TabsList>}
          <TabsContent value="consultant">
            <fieldset disabled={busy || consultantSent} className={styles.invitationFields}><div className={styles.formGrid}>
              <TextField id="consultant-name" label="Consultant Name" required value={values.consultantName} error={errors.consultantName} placeholder="Enter Consultant Name" onChange={(event) =>
                update("consultantName", capitalizeNameWords(event.target.value))
              } />
              <TextField id="consultant-email" label="Personal Email ID" type="email" required value={values.personalEmail} error={errors.personalEmail} placeholder="Enter Personal Email ID" onChange={(event) => update("personalEmail", event.target.value)} />
              <Select id="consultant-region" label="Region" disabled={Boolean(region)} required value={values.region} error={errors.region} placeholder="Select Region" options={[{ value: "INDIA", label: "India" }, { value: "US", label: "US" }]} onChange={(value) => update("region", value)} />
              {values.region === "US" && isVendor && <>
                <TextField id="consultant-agency" label="Agency Name" error={errors.agencyName}
                  required placeholder="Enter Agency Name" value={values.agencyName} onChange={(event) => update("agencyName", capitalizeNameWords(event.target.value))} />
                <TextField id="consultant-agency-contact" required label="Agency Contact Number" type="tel" placeholder="Enter Agency Contact Number" value={values.agencyContactNumber} error={errors.agencyContactNumber} onChange={(event) => update("agencyContactNumber", event.target.value)} />
              </>}
              <Select id="consultant-poc" label="Point of Contact (POC)" required searchable options={contacts.options} disabled={contacts.loading || busy || consultantSent} placeholder="Select Point of Contact" value={values.pointOfContact} error={errors.pointOfContact} onChange={(value) => update("pointOfContact", value)} />
              {/* Requestor Name is temporarily hidden.
          <Select id="consultant-requestor" label="Requestor Name" required searchable placeholder="Select Requestor Name" options={requestorOptions} value={values.requestorId} error={errors.requestorId} onChange={(value) => update("requestorId", value)} /> */}
            </div></fieldset>
          </TabsContent>
          {isVendor && <TabsContent value="agency">
            <fieldset disabled={busy} className={styles.invitationFields}><div className={styles.formGrid}>
              <TextField
                id="vendor-legal-company-name"
                label="Legal Company Name"
                placeholder="Enter Legal Company Name"
                required
                value={agency.legalCompanyName}
                error={errors.legalCompanyName}
                onChange={(event) => {
                  setAgency((current) => ({
                    ...current, legalCompanyName: capitalizeNameWords(
                      filterContactInput("legalCompanyName", event.target.value)
                    )
                  }));
                  setErrors((current) => ({ ...current, legalCompanyName: "" }));
                }}
              />
              <TextField id="vendor-contact-name" label="Agency Contact Name" placeholder="Enter Agency Contact Name" required value={agency.contactName} error={errors.contactName} onChange={(event) => { setAgency((current) => ({ ...current, contactName: filterContactInput("contactName", event.target.value) })); setErrors((current) => ({ ...current, contactName: "" })); }} />
              <TextField
                id="vendor-agency-email"
                label="Email Address"
                placeholder="Enter Email Address"
                type="email"
                required
                value={agency.agencyEmail}
                error={errors.agencyEmail}
                onChange={(event) => {
                  setAgency((current) => ({ ...current, agencyEmail: event.target.value }));
                  setErrors((current) => ({ ...current, agencyEmail: "" }));
                }}
              />
            </div></fieldset>
          </TabsContent>}
        </Tabs>
      </form>
    </Modal>
  );
}
